﻿import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Download, Save } from "lucide-react";
import { toast } from "react-toastify";

import Season from "@/components/Season/Season";
import Fetching from "@/components/Fetching/Fetching";
import CustomButton from "@/components/desktop/button/CustomButton";

import useUserStore from "@/stores/useUserStore";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import { useCustomer, useCustomerSave, useCustomerExcelImport } from "@/hooks/customers/useCustomerForm";
import { useSeasonDelete } from "@/hooks/customers/useSeason";
import useSyncedFilter from "@/hooks/general/useSyncedFilter";

import { baseCustomer } from "./customer.constants";
import { getToday } from "@/services/Utils";

import classes from "./CustomerPage.module.scss";

const tabs = [{ id: "seasons", label: "Temporadas" }];

const CustomerPage = () => {
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const inputRefExcel = useRef(null);
	const navigate = useNavigate();

	const { updateFilter } = useSyncedFilter();
	const user = useUserStore((state) => state.user);

	const [activeTab, setActiveTab] = useState("seasons");
	const [data, setData] = useState({ ...baseCustomer });
	const [logoFile, setLogoFile] = useState(null);

	const { data: response, isLoading } = useCustomer({ customer_id: user?.customer_id });
	const { mutate: saveCustomerData, isLoading: isLoadingSave } = useCustomerSave({ customer_id: user?.customer_id });
	const { mutate: deleteCustomerSeason, isLoading: isLoadingDelete } = useSeasonDelete();
	const { mutate: importExcel, isLoading: isLoadingExcelImport } = useCustomerExcelImport();

	useEffect(() => {
		if (response && response.customer) {
			setData(response.customer);
		}
	}, [response]);

	const selectSeason = (newSeason = null) => {
		const firstSeasonActive = data?.seasons?.find((season) => season.active);
		if (firstSeasonActive) {
			updateFilter({
				season_id: newSeason ? newSeason.id : firstSeasonActive.id,
			});
		}
	};

	const handleSave = () => {
		const formData = new FormData();

		for (const key in data) {
			if (Array.isArray(data[key])) {
				formData.append(key, JSON.stringify(data[key]));
			} else {
				formData.append(key, data[key] ?? "");
			}
		}

		if (logoFile) {
			formData.append("logo", logoFile);
		}

		saveCustomerData(formData, {
			onSuccess: (response) => {
				const newSeasons = response?.customer?.seasons || [];
				const newSeason = newSeasons.find((season) => season.active);

				selectSeason(newSeason);
				toast.success("Cliente guardado correctamente");
			},
		});
	};

	const handleDeleteSeason = (index) => {
		const season = data?.seasons[index];

		deleteCustomerSeason(
			{ selected: season },
			{
				onSuccess: () => {
					selectSeason();
					toast.success("Temporada eliminada correctamente");
					setData((prev) => {
						const updatedSeasons = [...prev.seasons];
						updatedSeasons.splice(index, 1);
						return { ...prev, seasons: updatedSeasons };
					});
				},
			},
		);
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const imageUrl = URL.createObjectURL(file);
			setData((prev) => ({ ...prev, logo: imageUrl }));
			setLogoFile(file);
		}
	};

	const handleExcelChange = () => {
		const file = inputRefExcel.current.files[0];
		const formData = new FormData();
		formData.append("file", file);

		importExcel(formData, {
			onSuccess: (response) => {
				if (inputRefExcel.current) {
					inputRefExcel.current.value = null;
				}
				if (response?.sheets && response?.message) {
					toast.success(response.message);
				}
			},
		});
	};

	const handleExcelDownload = () => {
		const fileName = `${getToday()} Excel Global ${data?.name || user?.customer_name}.xlsx`;
		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {},
					show: true,
					url: "/client/implementation/export_excel",
					name: fileName,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	if (isLoading || isLoadingSave || isLoadingDelete || isLoadingExcelImport) return <Fetching />;

	return (
		<div className={classes.wrapper}>
			<div className={classes.backWrapper}>
				<button className={classes.backButton} onClick={() => navigate(-1)}>
					<ArrowLeft size={18} className="mr-1" />
					Atrás
				</button>
			</div>

			<div className={classes.header}>
				<div className={classes.logoContainer} onClick={() => inputRef.current?.click()}>
					<img src={data?.logo || "https://placehold.co/120"} alt="Logo" className={classes.logo} />
					<input type="file" accept="image/*" ref={inputRef} style={{ display: "none" }} onChange={handleImageChange} />
				</div>
				<h1 className={classes.title}>{data?.name || ""}</h1>
			</div>

			<div className={classes.body}>
				<div className={classes.tabs}>
					{tabs.map((tab) => (
						<button
							key={tab.id}
							className={`${classes.tabButton} ${activeTab === tab.id ? classes.active : ""}`}
							onClick={() => setActiveTab(tab.id)}
						>
							{tab.label}
						</button>
					))}
				</div>

				<div className={classes.tabContent}>
					{activeTab === "seasons" && (
						<Season
							seasons={data?.seasons || []}
							setSeasons={(seasons) => setData({ ...data, seasons })}
							showTitle={false}
							handleDelete={handleDeleteSeason}
						/>
					)}
				</div>

				<div className={classes.actions}>
					<div className={classes.leftActions}>
						{!!user.superuser && (
							<>
								<CustomButton
									variant="secondary"
									size="sm"
									startIcon={<Download size={16} />}
									onClick={handleExcelDownload}
									disabled={isLoadingSave}
								>
									Descargar Excel
								</CustomButton>
								<CustomButton
									variant="secondary"
									size="sm"
									startIcon={<Upload size={16} />}
									onClick={() => inputRefExcel.current.click()}
									disabled={isLoadingSave}
								>
									Cargar Excel
								</CustomButton>
								<input ref={inputRefExcel} type="file" style={{ display: "none" }} onChange={() => handleExcelChange()} />
							</>
						)}
					</div>

					<CustomButton
						variant="primary"
						size="md"
						startIcon={<Save size={18} />}
						onClick={handleSave}
						disabled={isLoadingSave}
						loading={isLoadingSave}
					>
						Guardar cambios
					</CustomButton>
				</div>
			</div>
		</div>
	);
};

export default CustomerPage;
